import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiClient } from '../api.js';

interface AuthUser {
  id: string;
  email?: string;
}

export interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  api: ApiClient;
  children: ReactNode;
}

export function AuthProvider({ api, children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    api
      .getSession()
      .then(({ user: u }) => {
        if (!cancelled) {
          setUser(u);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error('[useAuth] getSession failed:', err);
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    const revalidate = () => {
      api
        .getSession()
        .then(({ user: u }) => setUser(u))
        .catch(() => {
          // Network errors during revalidation are non-fatal; surface stays as-is.
        });
    };
    window.addEventListener('visibilitychange', revalidate);
    window.addEventListener('focus', revalidate);
    return () => {
      window.removeEventListener('visibilitychange', revalidate);
      window.removeEventListener('focus', revalidate);
    };
  }, [api]);

  const signIn = useCallback(() => {
    const redirect = window.location.href;
    window.location.href = `${api.baseUrl}/api/auth/login?redirect=${encodeURIComponent(redirect)}`;
  }, [api]);

  useEffect(() => {
    const handler = () => {
      queryClient.clear();
      setUser(null);
      // signIn() does a full-page navigation; don't await.
      signIn();
    };
    window.addEventListener('deckspace:unauthorized', handler);
    return () => window.removeEventListener('deckspace:unauthorized', handler);
  }, [signIn, queryClient]);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      queryClient.clear();
      setUser(null);
    }
  }, [api, queryClient]);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [user, loading, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
